import React, { useState, useEffect } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";

import { useRecoilState } from 'recoil';
import { outletAtom, subAtom } from 'core/config/atoms';

import { motion } from "framer-motion";
import Lottie from 'lottie-react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import ServiceOutlet from 'services/serviceOutlet';

import TopBar from 'core/widgets/ui/TopBar';

import animBtnLoading from 'assets/anim/anim-btnLoading.json';

export default function PanelTax(props) {

    const [outlet] = useRecoilState(outletAtom);
    const [sub] = useRecoilState(subAtom);

    const [doc, setDoc] = useState({});

    const [submit, setSubmit] = useState(false);

    const outletService = new ServiceOutlet();

    const taxTypes = [
        { value: 'na', label: 'NA' },
        { value: 'inclusive', label: 'Inclusive' },
        { value: 'exclusive', label: 'Exclusive' },
        { value: 'overall', label: 'Overall' },
    ];

    const formVSchema = Yup.object().shape({
        gstn: Yup.string().required('This information is required'),
        floating: Yup.number().required('This information is required'),
        currency: Yup.string().required('This information is required'),
        foodp: Yup.number().required('This information is required'),
        roomp: Yup.number().required('This information is required'),
        foodptype: Yup.string().required('This information is required'),
        roomptype: Yup.string().required('This information is required'),
    });

    const { values, errors, touched, handleChange, handleSubmit, handleReset, resetForm, setErrors, setValues } = useFormik({
        initialValues: {
            gstn: '',
            floating: '',
            currency: '',
            foodp: '',
            roomp: '',
            foodptype: '',
            roomptype: '',
        },
        validationSchema: formVSchema,
        enableReinitialize: true,
        onSubmit: values => {
            setSubmit(true);
            let body = {
                uid: doc._id,
                data: {
                    "tax": {
                        isGST: doc.tax.isGST,
                        floatingPoint: values.floating,
                        GSTN: values.gstn,
                        Currency: values.currency,
                        Food_Percentage: values.foodp,
                        Food_TAX_TYPE: values.foodptype,
                        Room_Percentage: values.roomp,
                        Room_TAX_TYPE: values.roomptype,
                    },
                }
            }
            outletService.updateOutletById(body).then((res) => {
                setSubmit(false);
                if (res.status) {
                    toast.success("Tax Settings Updated!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    loadData();
                    handleReset();
                    resetForm();
                } else {
                    toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                }
            });
        }
    });

    const loadData = () => {
        (async function () {
            var res = await outletService.getOutletById({ 'outletid': outlet.outletid.outletid });
            setDoc(res.data);
            setValues({
                gstn: res.data.tax.GSTN === "na" ? "" : res.data.tax.GSTN,
                floating: res.data.tax.floatingPoint,
                currency: res.data.tax.Currency,
                foodp: res.data.tax.Food_Percentage,
                roomp: res.data.tax.Room_Percentage,
                foodptype: res.data.tax.Food_TAX_TYPE,
                roomptype: res.data.tax.Room_TAX_TYPE,
            })
        })();
    }

    useEffect(() => {
        loadData();
    }, []);

    return (
        <ScrollArea className='w-full h-full py-3 px-3'>
            <div className="w-full h-full">
                <div className="grid grid-cols-12 grid-gap-2">
                    <div className="col-span-12 mt-2">
                        <div className="intro-y flex items-center justify-between h-10 border-b pb-4">
                            <h2 className="text-2xl font-medium font-serif truncate ml-2 text-ascent">
                                <motion.i whileHover={{ scale: 1.2 }} className="las la-bars cursor-pointer" onClick={() => { props.onToggle(); }}></motion.i> Tax Settings
                            </h2>
                            <TopBar />
                        </div>
                    </div>
                    <div className="col-span-12 mt-2">
                        <form className='mt-4 grid grid-cols-12 gap-3' onSubmit={handleSubmit} onReset={handleReset} noValidate="" autoComplete='off'>
                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">GSTN Number:<sup className="text-red-600">*</sup></label>
                                <input type="text" id="gstn" value={values.gstn} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxx" onChange={handleChange} />
                                {(errors.gstn && touched.gstn) && <p className='text-xs text-red-400 mt-1'>{errors.gstn}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Floating Point:<sup className="text-red-600">*</sup></label>
                                <input type="number" id="floating" value={values.floating} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxx" onChange={handleChange} />
                                {(errors.floating && touched.floating) && <p className='text-xs text-red-400 mt-1'>{errors.floating}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Currency Symbol:<sup className="text-red-600">*</sup></label>
                                <input type="text" id="currency" value={values.currency} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. x" onChange={handleChange} />
                                {(errors.currency && touched.currency) && <p className='text-xs text-red-400 mt-1'>{errors.currency}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Food Tax Percentage (%):<sup className="text-red-600">*</sup></label>
                                <input type="number" id="foodp" value={values.foodp} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxx" onChange={handleChange} />
                                {(errors.foodp && touched.foodp) && <p className='text-xs text-red-400 mt-1'>{errors.foodp}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block text-xs font-medium text-gray-700 font-mono">Food Tax Type<sup className="text-red-600">*</sup></label>
                                <select id="foodptype" className="border-gray-300 border rounded appearance-none w-full py-1 px-2 bg-white mt-2" onChange={handleChange} value={values.foodptype}>
                                    <option value="" disabled="" selected="selected">Select</option>
                                    {
                                        taxTypes.length !== 0 && taxTypes.map((item) => {
                                            return <option value={item.value} selected={values.foodptype == item.value}>{item.label}</option>
                                        })
                                    }
                                </select>
                                {(errors.foodptype && touched.foodptype) && <p className='text-xs text-red-400 mt-1'>{errors.foodptype}</p>}
                            </div>

                            <div className="col-span-4"></div>

                            {
                                sub.features.isRoom && <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Room Tax Percentage (%):<sup className="text-red-600">*</sup></label>
                                    <input type="number" id="roomp" value={values.roomp} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxx" onChange={handleChange} />
                                    {(errors.roomp && touched.roomp) && <p className='text-xs text-red-400 mt-1'>{errors.roomp}</p>}
                                </div>
                            }

                            {
                                sub.features.isRoom && <div className="col-span-4">
                                    <label className="block text-xs font-medium text-gray-700 font-mono">Room Tax Type<sup className="text-red-600">*</sup></label>
                                    <select id="roomptype" className="border-gray-300 border rounded appearance-none w-full py-1 px-2 bg-white mt-2" onChange={handleChange} value={values.roomptype}>
                                        <option value="" disabled="" selected="selected">Select</option>
                                        {
                                            taxTypes.length !== 0 && taxTypes.map((item) => {
                                                return <option value={item.value} selected={values.roomptype == item.value}>{item.label}</option>
                                            })
                                        }
                                    </select>
                                    {(errors.roomptype && touched.roomptype) && <p className='text-xs text-red-400 mt-1'>{errors.roomptype}</p>}
                                </div>
                            }

                            <div className='col-span-12 my-6'>
                                <div className='flex items-end'>
                                    <button type="submit" className="text-white bg-prime hover:bg-primeLight focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center" disabled={submit}>
                                        {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" loop={true} />}
                                        {!submit && "SAVE"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </ScrollArea>
    )
}
